import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        try {
          // Verify token is still valid
          const userData = await authApi.getMe();
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authApi.login(email, password);
    localStorage.setItem('token', response.access_token);
    localStorage.setItem('user', JSON.stringify(response.user));
    setUser(response.user);
    setIsAuthenticated(true);
    return response;
  };

  const refreshUser = async () => {
    try {
      const userData = await authApi.getMe();
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('active_venue_id');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Helper per verificare i permessi in base al ruolo
  const isAdmin = () => user?.ruolo === 'admin';
  const isManager = () => user?.ruolo === 'manager';
  const isOperatore = () => user?.ruolo === 'operatore_cassa';
  const isAnalista = () => user?.ruolo === 'analista';
  const isSuperAdmin = () => !!user?.is_super_admin;
  const mustChangePassword = () => !!user?.must_change_password;
  
  // Permessi per funzionalità specifiche
  const canManageUsers = () => isAdmin() || isSuperAdmin();
  const canManageLocali = () => isAdmin() || isSuperAdmin();
  const canViewDashboard = () => ['admin', 'manager', 'analista'].includes(user?.ruolo);
  const canModifyChiusure = () => ['admin', 'manager', 'operatore_cassa'].includes(user?.ruolo);
  const canViewUscite = () => ['admin', 'manager'].includes(user?.ruolo);
  const canModifyUscite = () => ['admin', 'manager'].includes(user?.ruolo);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login,
        logout,
        refreshUser,
        // Ruoli
        isAdmin,
        isManager,
        isOperatore,
        isAnalista,
        isSuperAdmin,
        mustChangePassword,
        // Permessi
        canManageUsers,
        canManageLocali,
        canViewDashboard,
        canModifyChiusure,
        canViewUscite,
        canModifyUscite,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
